import { MonoBehavior } from 'game/core/monoBehavior';
import { Container, FederatedPointerEvent, Point, Text } from 'pixi.js';
import { CardDisplay } from './CardDisplay';
import gsap, { Power1 } from 'gsap';
import { GameEvent } from 'game/events/gameEvent';
import { EventName } from 'game/events/eventsName';
import { SoundManager } from 'game/core/soundManager';
import { GamePlace, CardColors, CardPlace } from 'constants/cards';
import { CARD_MOVE_TIME } from 'constants/move';
import { DROP_Z_INDEX, MOVE_Z_INDEX } from 'constants/layout';
import { Store } from 'game/store/gameStore';
import { GameName } from 'constants/difficulty';

let lastMove = 0;
let lastMoveZIndex = DROP_Z_INDEX;

function getMoveZIndex() {
    if (performance.now() - lastMove > 2000) {
        lastMoveZIndex = MOVE_Z_INDEX;
    }
    lastMove = performance.now();
    lastMoveZIndex++;
    return lastMoveZIndex;
}

let lastDrop = 0;
let lastDropZIndex = DROP_Z_INDEX;
function getDropZIndex() {
    if (performance.now() - lastDrop > 2000) {
        lastDropZIndex = DROP_Z_INDEX;
    }
    lastDrop = performance.now();
    lastDropZIndex++;
    return lastDropZIndex;
}

export class Card extends MonoBehavior {
    cardDisplay: CardDisplay;
    container: Container;
    value = 0;
    code: string;
    visible = false;
    active = false;
    position: Point;
    cardColor: CardColors;
    place: CardPlace;
    moveTween = null as null | { tween: gsap.core.Tween; then: () => void };
    onTable = false;
    isHighlighted = false;
    inTutorial = false;
    // debugText: Text;
    constructor(code: string) {
        super();

        this.startDrag = this.startDrag.bind(this);
        this.triggerClick = this.triggerClick.bind(this);
        this.container = new Container();
        this.code = code;
        this.cardColor = this.getCardColor(code);
        this.value = this.getCardValue(code);
        this.cardDisplay = new CardDisplay(this);
        this.container.eventMode = 'static';
        this.container.on('pointerdown', this.startDrag);
        this.position = new Point(0, 0);
        this.container.position = this.position;
        this.place = { cardPlace: GamePlace.STOCKPILE, value: 0, position: new Point(), zIndex: 0, order: 0 };
    }
    private getCardColor(code: string) {
        return code[1] as CardColors;
    }
    private getCardValue(code: string) {
        switch (code[0]) {
            case 'A':
                return 1;
            case 'J':
                return 11;
            case 'Q':
                return 12;
            case 'K':
                return 13;
            case 'T':
                return 10;
            default:
                return parseInt(code[0]);
        }
    }
    setVisible(show: boolean, quite = false, noAnimation = false) {
        if (this.visible !== show && !quite) SoundManager.play('flipCardSFX');

        this.visible = show;
        this.cardDisplay.setVisible(show, noAnimation);
        if (!this.visible) this.active = false;
    }
    dealFromStockPile() {
        this.visible = true;
        this.cardDisplay.dealFromStockPile();
    }
    changeVisible(show: boolean) {
        this.cardDisplay.setVisible(show, true);
    }

    setActive(active: boolean): void {
        this.active = active;
    }

    takeUp() {
        this.onTable = false;
        const zIndex = getMoveZIndex();
        this.container.zIndex = zIndex;
        this.cardDisplay.hideOverlay();
    }

    dropDown() {
        const zIndex = getDropZIndex();
        this.container.zIndex = zIndex;
    }

    moveTo(
        place: Point,
        settings?: { delay?: number; duration?: number; ease?: gsap.EaseFunction },
        cb = () => {},
        onStartMove?: () => void,
    ) {
        if (onStartMove) setTimeout(onStartMove, (settings?.delay || 0) * 1000);

        const gsapSettings = {
            delay: settings?.delay || 0,
            duration: settings?.duration || CARD_MOVE_TIME,
            ease: settings?.ease || Power1.easeOut,
        };
        this.moveTween?.tween.kill();

        const tween = gsap.to(this.container.position, {
            ...place,
            ...gsapSettings,
        });

        this.moveTween = {
            tween,
            then: () => {
                cb?.();
            },
        };
        tween.then(() => {
            this.reset();
            cb?.();
        });

        return gsapSettings;
    }
    setPosition(cardPosition: Point) {
        this.container.position = cardPosition;
        this.place.position = cardPosition;
        this.position = cardPosition;
    }

    startDrag(e: FederatedPointerEvent) {
        console.log(`[CARD DRAG] Starting drag for card: ${this.code}(${this.value})`);
        console.log(`[CARD DRAG] Card ${this.code} active: ${this.active}, visible: ${this.visible}`);
        
        if (Store.isSpider) this.activeAnimation();

        if (this.active) {
            console.log(`[CARD DRAG] Card ${this.code} is active - emitting DRAG_CARD event`);
            GameEvent.emit(EventName.DRAG_CARD, { card: this, e });
        } else {
            console.log(`[CARD DRAG] Card ${this.code} is NOT active - emitting DRAG_DISABLED_CARD event`);
            GameEvent.emit(EventName.DRAG_DISABLED_CARD, { card: this, e });
        }
    }
    triggerClick() {
        this.activeAnimation();
        GameEvent.emit(EventName.AUTO_PLAY, { card: this });
    }
    setPlace(newPlace: { cardPlace: GamePlace; value: number; position: Point; zIndex: number; order: number }) {
        this.place = { ...newPlace };
    }
    get isRed() {
        return [CardColors.DIAMONDS, CardColors.HEARTS].includes(this.cardColor);
    }
    canDropInColumn(droppedCard: Card) {
        if (!this.visible) return false;

        switch (Store.name) {
            case GameName.KLONDIKE:
            case GameName.FREECELL:
            case GameName.PYRAMID:
                return this.isRed !== droppedCard.isRed && this.value - 1 === droppedCard.value;
            case GameName.SPIDER:
                return this.value - 1 === droppedCard.value;
        }
        return false;
    }
    canDropInFoundation(droppedCard: Card) {
        return this.cardColor === droppedCard.cardColor && this.value + 1 === droppedCard.value;
    }

    destroy(): void {
        super.destroy();
        this.container.removeFromParent();
        this.cardDisplay.destroy();
    }

    reset() {
        this.onTable = true;
        if (!this.inTutorial) this.container.zIndex = this.place.zIndex;
        this.container.position.set(this.place.position.x, this.place.position.y);
        this.position = this.container.position.clone();
    }
    shouldMove(pos: Point) {
        return Math.abs(this.position.x - pos.x) > 0 || Math.abs(this.position.y - pos.y) > 0;
    }
    inActiveAnimation() {
        if (this.visible) this.cardDisplay.blockOverlayPop();
    }

    highlight() {
        this.isHighlighted = true;
        this.cardDisplay.highlight();
    }

    resize(): void {
        if (this.isHighlighted) {
            this.resetHighlight();
            this.highlight();
        }
    }

    resetHighlight() {
        this.isHighlighted = false;
        this.cardDisplay.resetHighlight();
    }

    activeAnimation() {
        this.cardDisplay.hideOverlay();
    }
    shakeCard() {
        this.onTable = false;
        this.cardDisplay.shakeCard();
    }
    get codeName() {
        let color = '';
        switch (this.cardColor) {
            case CardColors.CLUBS:
                color = 'CLUBS';
                break;
            case CardColors.DIAMONDS:
                color = 'DIAMONDS';
                break;
            case CardColors.HEARTS:
                color = 'HEARTS';
                break;
            case CardColors.SPADES:
                color = 'SPADES';
                break;
        }
        return `${this.value} ${color}`;
    }
}
